import { IRoute } from "./navigation-types";

/**
 * Calculate the effective priority order for a route based on enabled feature flags
 * @param params - The parameters object
 * @param params.route - The route object with base priorityOrder and optional featureFlagPriority
 * @param params.enabledFlags - Array of enabled feature flag names
 * @returns The route with effective priorityOrder applied
 */
export const getEffectiveRouteWithPriority = ({
    route,
    enabledFlags
}: {
    route: IRoute;
    enabledFlags: string[];
}): IRoute => {
    let effectivePriority = route.priorityOrder;

    // Check if any feature flags override the priority
    if (route.featureFlagPriority) {
        enabledFlags.forEach((flag) => {
            if (route.featureFlagPriority?.[flag] !== undefined) {
                effectivePriority = route.featureFlagPriority[flag];
            }
        });
    }

    return { ...route, priorityOrder: effectivePriority };
};

/**
 * Process an array of routes and apply feature flag priority overrides
 * @param params - The parameters object
 * @param params.routes - Array of route objects
 * @param params.enabledFlags - Array of enabled feature flag names
 * @returns Array of routes with effective priorities applied, sorted by priority
 */
export const getEffectiveRoutesWithPriorities = ({
    routes,
    enabledFlags
}: {
    routes: IRoute[];
    enabledFlags: string[];
}): IRoute[] => {
    return routes
        .map((route) => getEffectiveRouteWithPriority({ route, enabledFlags }))
        .sort((a, b) => {
            // Primary sort by priority order
            if (a.priorityOrder !== b.priorityOrder) {
                return a.priorityOrder - b.priorityOrder;
            }

            // Secondary sort by route name for deterministic ordering when priorities are equal
            return a.name.localeCompare(b.name);
        });
};
